import { useEffect, useMemo, useState } from 'react'
import { usePartialTranscriptions } from '@renderer/hooks/audio/use-partial-transcriptions'
import type { PartialTranscription } from '@shared/transcript-feed'
import { useIsDictating, useIsVoiceModeActive } from '../../../../store/atoms'

const MAX_PLACEHOLDER_CHARS = 60

/**
 * While dictating, the composer's placeholder tails the live transcript instead
 * of its normal prompt. Partials arrive keyed by start timestamp: a repeat of
 * the same timestamp revises that segment, a new one appends.
 */
export const useDictationPlaceholder = (placeholder?: string) => {
  const isDictating = useIsDictating()
  const isVoiceModeActive = useIsVoiceModeActive()
  const isTranscribing = isDictating || isVoiceModeActive
  // Only subscribe to the partials broadcast while actually transcribing —
  // an idle chat input receives no partial-transcript IPC at all.
  const { microphone: partial } = usePartialTranscriptions(isTranscribing)
  const [segments, setSegments] = useState<Array<PartialTranscription>>([])

  useEffect(() => {
    setSegments([])
  }, [isTranscribing])

  useEffect(() => {
    if (!partial?.text) return
    setSegments((prev) =>
      prev[prev.length - 1]?.startTimestamp === partial.startTimestamp
        ? [...prev.slice(0, -1), partial]
        : [...prev, partial],
    )
  }, [partial])

  return useMemo(() => {
    if (!isTranscribing) return placeholder
    if (!segments.length) return 'Asking Highlight with voice...'
    const transcript = segments.map((segment) => segment.text).join(' ')
    return transcript.slice(-MAX_PLACEHOLDER_CHARS)
  }, [segments, isTranscribing, placeholder])
}
